import { Tweet } from './tweetService';
import { callAI } from './aiService';

export interface StyleModel {
  systemPrompt: string;
  exampleCount: number;
  createdAt: string;
  exampleTweets?: string[]; // 実際のツイート例
  frequentPhrases?: string[]; // 頻出表現
  toneAnalysis?: string; // トーン分析
}

export interface AnalyzeStyleResponse {
  success: boolean;
  styleModel: StyleModel;
  message: string;
}

export const styleService = {
  async analyzeStyle(
    userId: string,
    tweets: Tweet[]
  ): Promise<AnalyzeStyleResponse> {
    try {
      // エンゲージメント順にソート
      const sortedTweets = tweets
        .sort((a, b) => {
          const engagementA = (a.likes || 0) + (a.retweets || 0);
          const engagementB = (b.likes || 0) + (b.retweets || 0);
          return engagementB - engagementA;
        });

      // 上位100件（高エンゲージ）と最新50件（現在の文体）を組み合わせ
      const topEngagement = sortedTweets.slice(0, 100).map(t => t.text);
      const recentTweets = tweets.slice(0, 50).map(t => t.text);
      
      const allTweets = Array.from(new Set([...topEngagement, ...recentTweets])); // 重複削除

      if (allTweets.length < 10) {
        throw new Error('文体分析には最低10件のツイートが必要です');
      }

      // 分析用サンプル（上位30件 + ランダム10件）
      const topSamples = allTweets.slice(0, 30);
      const randomSamples = allTweets
        .slice(30)
        .sort(() => Math.random() - 0.5)
        .slice(0, 10);
      
      const analysisSamples = [...topSamples, ...randomSamples];
      const exampleTweets = analysisSamples.join('\n---\n');

      // 頻出表現の抽出（クライアント側で簡易分析）
      const frequentPhrases = extractFrequentPhrases(allTweets);

      // AI APIで文体分析（より詳細な指示）
      const analysisPrompt = `以下は、あるTwitterユーザーの過去${allTweets.length}件のツイート（高エンゲージ + 最新）から抽出した${analysisSamples.length}件です。
このユーザーの文体を徹底的に分析し、新しいツイートを生成する際に使う「システムプロンプト」を作成してください。抽象表現は避け、**具体例と数値**を入れてください。

# ツイート例
${exampleTweets}

# 頻出表現（参考）
${frequentPhrases.slice(0, 10).join(', ')}

# 分析項目（数値・割合も入れる）
1. **語尾・口癖**：上位5個。使用率（〇％）と実例
2. **絵文字の使い方**：平均使用数/ツイート、配置（文頭/文中/文末）、よく使う絵文字
3. **文の長さ**：平均文字数と主なレンジ（短文/中文/長文）。行数の傾向
4. **句読点**：「。」「、」の頻度、「！」「？」の使い方と強調パターン
5. **改行**：使用率と典型的な行構成（例：2行構成、3行構成）
6. **トーン**：カジュアル/フォーマル、ポジティブ/ニュートラル/ネガティブ、断定/婉曲の度合い
7. **フック/締め**：書き出しの型（結論/問い/体験）と末尾フレーズのパターン
8. **具体性**：数字・固有名詞・体験談の頻度
9. **一人称/主語**：「俺」「僕」「私」「自分」などの使用状況
10. **特徴的な表現**：接続詞・副詞・感嘆詞の例

# 出力形式
以下のテンプレートだけで返してください（説明不要）。

あなたは、[ユーザーの特徴を一言で]。

**文体的特徴（数値付き）**
- 語尾・口癖：上位5個 / 使用率と実例
- 絵文字：平均使用数、配置、よく使う絵文字
- 文の長さ・行数：平均XX字、短/中/長の割合、改行の典型
- 句読点：頻度と使い分け
- フック/締め：書き出しと終わり方のパターン（疑問/共感/結論/CTAなど）
- トーン：カジュアル/フォーマル、ポジティブ度、断定度
- 一人称/主語：使う表現
- 具体性：数字・固有名詞・体験の頻度

**書き方のルール**
1. 文体を再現する具体的手順（語尾・改行・絵文字の配置など）
2. 生成時に守る字数・具体性・フック多様化の指針
3. AI臭回避のための禁止語・言い換え方

**絶対に避けること**
- 広告/テンプレっぽい定型句
- 過剰な絵文字・「!!!??」連打
- 語尾や締めの連続パターン

このスタイルで、自然で本人らしいツイートを生成してください。`;

      const systemPrompt = await callAI(
        [
          { 
            role: 'system', 
            content: `あなたは、文体分析の専門家です。ユーザーのツイートから、口癖・語尾・句読点の使い方・絵文字の使用頻度など、細かい文体的特徴を正確に抽出し、再現可能なシステムプロンプトを作成します。

**重要：**
- 抽象的な表現ではなく、具体的な例と数値（割合・平均値）を含めること
- 「丁寧」「カジュアル」だけでなく、実際の語尾（「〜だね」「〜ですね」など）を明記
- 数値的な傾向（「平均150字」「絵文字1-2個」「改行率40%」など）も含める` 
          },
          { role: 'user', content: analysisPrompt }
        ],
        { temperature: 0.6, maxTokens: 1500 }
      );

      // トーン分析（簡易版）
      const toneAnalysis = analyzeTone(allTweets);

      // 実際のツイート例を保存（Few-shot用に上位10件）
      const exampleTweetsForFewShot = topSamples.slice(0, 10);

      const styleModel: StyleModel = {
        systemPrompt,
        exampleCount: allTweets.length,
        exampleTweets: exampleTweetsForFewShot, // 実際のツイート例を保存
        frequentPhrases: frequentPhrases.slice(0, 20),
        toneAnalysis,
        createdAt: new Date().toISOString()
      };

      // localStorageに保存（generateServiceで使用）
      localStorage.setItem('style_model', JSON.stringify(styleModel));

      return {
        success: true,
        styleModel,
        message: '文体分析が完了しました'
      };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('API key')) {
          throw new Error('AI API Keyが無効です。設定を確認してください。');
        }
        throw error;
      }
      throw new Error('文体分析に失敗しました');
    }
  },
};

// 頻出表現を抽出（2文字以上のフレーズ）
function extractFrequentPhrases(tweets: string[]): string[] {
  const phraseCount = new Map<string, number>();
  
  tweets.forEach(tweet => {
    // 2-5文字のフレーズを抽出
    for (let len = 2; len <= 5; len++) {
      for (let i = 0; i <= tweet.length - len; i++) {
        const phrase = tweet.substring(i, i + len);
        // ひらがな・カタカナ・漢字のみ（記号・数字は除外）
        if (/^[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]+$/.test(phrase)) {
          phraseCount.set(phrase, (phraseCount.get(phrase) || 0) + 1);
        }
      }
    }
  });

  // 出現回数でソート（3回以上）
  return Array.from(phraseCount.entries())
    .filter(([_, count]) => count >= 3)
    .sort((a, b) => b[1] - a[1])
    .map(([phrase, _]) => phrase);
}

// トーン分析（ポジティブ/ネガティブ/ニュートラル）
function analyzeTone(tweets: string[]): string {
  let positiveCount = 0;
  let negativeCount = 0;

  const positiveWords = ['嬉しい', '楽しい', '最高', 'いい', 'すごい', '良い', '好き', 'ありがとう', '感謝'];
  const negativeWords = ['悲しい', 'つらい', '最悪', '嫌', 'ダメ', '困る', '疲れ', '不安'];

  tweets.forEach(tweet => {
    const lower = tweet.toLowerCase();
    positiveWords.forEach(word => {
      if (lower.includes(word)) positiveCount++;
    });
    negativeWords.forEach(word => {
      if (lower.includes(word)) negativeCount++;
    });
  });

  const total = positiveCount + negativeCount;
  if (total === 0) return 'ニュートラル';

  const positiveRatio = positiveCount / total;
  if (positiveRatio > 0.6) return 'ポジティブ寄り';
  if (positiveRatio < 0.4) return 'ネガティブ寄り';
  return 'バランス型';
}
